import { Button } from '@staff-app/ui';
import { Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  CustomerDocument,
  DOC_TYPE_LABEL,
  DocType,
  UploadFields,
  documentsApi,
} from '@/features/customers/lib/documents-api';

/** Same allowlist the Document Center dialog uses; the server's magic-byte sniff is the gate. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.tif,.tiff,.doc,.docx,.xls,.xlsx,.pptx';
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  /** The card's Stripe customer id field. The server resolves it to an accountId. */
  stripeId: string;
  keyId: string;
  /**
   * What the file IS. Defaults to 'po' because that is what the invoice cards produce; a
   * card that files something else (a tax-exempt certificate, say) passes its own type so
   * the document lands searchable under it in the Document Center.
   */
  docType?: DocType;
  /** Defaults to the type tag alone — the same seed the upload dialog starts you with. */
  tags?: string[];
  /**
   * Label-only. The uploader itself never blocks: whether a missing document should stop
   * the card's action is the CARD's `valid` gate to decide (and the server's to enforce),
   * so this flag just stops the label from calling a mandatory document optional.
   */
  required?: boolean;
  /**
   * Metadata to file alongside the bytes — dates, jurisdiction, counterparty. Cards that
   * know these up front (an expiry the operator already typed) pass them here so the
   * document does not land needing a second edit pass in the Document Center.
   */
  extraFields?: Pick<
    UploadFields,
    | 'effectiveDate'
    | 'expirationDate'
    | 'jurisdiction'
    | 'externalNumber'
    | 'counterpartyName'
    | 'notes'
  >;
  /**
   * Called with the stored document's id once the upload lands.
   *
   * The card uses it to back-fill the invoice id AFTER its own action succeeds — the file
   * is filed before an invoice exists, so the link between the two can only be made in
   * that direction.
   */
  onAttached?: (documentId: string) => void;
  /**
   * The whole stored row, for cards that need more than the id — status, verifiedAt, title —
   * to fold the new document into a picker they already render. Fires alongside `onAttached`.
   */
  onUploaded?: (doc: CustomerDocument) => void;
}

/**
 * Optional document attachment for a Billing Console card.
 *
 * Files the document against the CUSTOMER, not the invoice: it uploads as soon as you pick
 * it, independent of whether the card's main action succeeds. That is deliberate — a PO or
 * contract is a real document the moment it arrives, and tying it to a Stripe call that
 * might fail validation would mean re-attaching it on every retry.
 *
 * Stored as docType 'po' BY DEFAULT, which is what the invoice cards produce in practice —
 * every type-specific prop is optional so those two call sites read exactly as they did
 * before this became reusable. Cards filing something else (the Apply Coupon card's
 * tax-exempt certificate) pass `docType`, and with it the tags, label and toast follow.
 * The LABEL stays generic ("Document attachment") because the accepted formats never were
 * PO-only, and because "reference" would collide with the upload dialog's "Reference
 * number" field — one word doing two different jobs a few clicks apart.
 *
 * Uploads with the card's Stripe id; the server resolves that to an accountId and stores
 * only the accountId, because a Stripe id changes on churn-and-resubscribe while the
 * account does not.
 */
export function DocumentAttachment({
  stripeId,
  keyId,
  docType = 'po',
  // Evaluated after docType above, so the seed tag tracks whatever type the card chose.
  tags = [docType],
  required = false,
  extraFields,
  onAttached,
  onUploaded,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [attached, setAttached] = useState<{ name: string } | null>(null);

  const trimmed = stripeId?.trim() ?? '';
  const ready = /^cus_/i.test(trimmed);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPT.split(',').includes(ext)) {
      toast.error('Unsupported file type', { description: `Allowed: ${ACCEPT}` });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', { description: 'The limit is 25 MB.' });
      return;
    }
    setBusy(true);
    try {
      const { document } = await documentsApi.upload(
        {
          stripeId: trimmed,
          docType,
          title: file.name.replace(/\.[^.]+$/, ''),
          tags,
          ...extraFields,
        },
        file,
      );
      setAttached({ name: file.name });
      onAttached?.(document.id);
      onUploaded?.(document);
      toast.success(`${DOC_TYPE_LABEL[docType]} attached to the customer`, {
        description: file.name,
      });
    } catch (e: unknown) {
      toast.error('Could not attach the document', {
        description: (e as Error)?.message ?? 'Unknown error',
      });
    } finally {
      setBusy(false);
      // Allow re-picking the same filename after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid={`billing-${keyId}-doc-attach`}>
      {/* Mirrors LabeledInput's shape — label, control, helper — so it reads as another
          field in the form rather than a widget bolted onto the end of the card. */}
      <span className="text-xs font-medium text-foreground">
        {'Document attachment'}
        <span className="ml-1 font-normal text-muted-foreground">
          {required ? '(required)' : '(optional)'}
        </span>
      </span>

      <div
        className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2.5 transition-colors ${
          attached
            ? 'border-primary/40 bg-primary/5'
            : ready
              ? 'border-input'
              : 'border-dashed border-muted-foreground/30 bg-muted/30'
        }`}
      >
        <Paperclip
          className={`h-4 w-4 shrink-0 ${attached ? 'text-primary' : 'text-muted-foreground'}`}
        />

        <span className="min-w-0 flex-1 truncate text-xs">
          {attached ? (
            <>
              <span className="font-medium text-foreground">{attached.name}</span>
              <span className="text-muted-foreground">{' filed against the customer'}</span>
            </>
          ) : (
            <span className="text-muted-foreground">
              {'PDF, image, Word or Excel · max 25 MB'}
            </span>
          )}
        </span>

        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 text-xs"
          disabled={!ready || busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`billing-${keyId}-doc-attach-btn`}
        >
          {busy ? 'Uploading…' : attached ? 'Replace' : 'Choose file'}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground" data-testid={`billing-${keyId}-doc-attach-hint`}>
        {ready
          ? `Filed against this customer as a ${DOC_TYPE_LABEL[docType]} document as soon as you choose it — it does not wait for the action above.`
          : 'Enter the Customer Stripe ID above first: it decides which customer the file is filed under.'}
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={`billing-${keyId}-doc-attach-input`}
        onChange={(e) => void onPick(e.target.files?.[0] ?? undefined)}
      />
    </div>
  );
}
